import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Room, Task, getTaskText, normalizeTask } from "@/types/checklist";
import { getStoredLanguage } from "@/lib/languages";

// Session cache for translated checklist content
const checklistTranslationCache = new Map<string, Map<string, string>>();

interface TranslatedChecklist {
  name: string;
  rooms: Room[];
}

export const useDynamicChecklistTranslation = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Get or create language-specific cache
  const getLanguageCache = useCallback((lang: string) => {
    if (!checklistTranslationCache.has(lang)) {
      checklistTranslationCache.set(lang, new Map());
    }
    return checklistTranslationCache.get(lang)!;
  }, []);

  // Translate a single checklist with all its content
  const translateChecklist = useCallback(async (
    checklistName: string,
    rooms: Room[],
    targetLanguage?: string
  ): Promise<TranslatedChecklist> => {
    const lang = targetLanguage || getStoredLanguage();
    
    // English needs no translation
    if (lang === 'en') {
      return { name: checklistName, rooms };
    }

    const cache = getLanguageCache(lang);
    
    // Gather all texts to translate
    const textsToTranslate: string[] = [];
    
    // Add checklist name
    if (checklistName && !cache.has(checklistName)) {
      textsToTranslate.push(checklistName);
    }
    
    // Add room names and task texts
    rooms.forEach(room => {
      if (room.name && !cache.has(room.name)) {
        textsToTranslate.push(room.name);
      }
      room.tasks.forEach(task => {
        const taskObj = normalizeTask(task);
        if (taskObj.text && !cache.has(taskObj.text)) {
          textsToTranslate.push(taskObj.text);
        }
      });
    });

    // If all texts are cached, return immediately
    if (textsToTranslate.length === 0) {
      return applyTranslations(checklistName, rooms, cache);
    }

    setIsTranslating(true);

    try {
      // First check Supabase cache
      const { data: dbCached } = await supabase
        .from('translations_cache')
        .select('source_text, translated_text')
        .eq('language', lang)
        .in('source_text', textsToTranslate);

      // Add DB cached results to session cache
      if (dbCached) {
        dbCached.forEach(c => {
          cache.set(c.source_text, c.translated_text);
        });
      }

      // Find texts still needing translation
      const uncachedTexts = textsToTranslate.filter(t => !cache.has(t));

      if (uncachedTexts.length > 0) {
        // Call translation API for uncached texts
        const { data, error } = await supabase.functions.invoke('translate-text', {
          body: { texts: uncachedTexts, targetLanguage: lang }
        });

        if (!error && data?.translations) {
          Object.entries(data.translations).forEach(([source, translated]) => {
            cache.set(source, translated as string);
          });
        } else {
          console.warn('Translation API error:', error);
          // Use originals as fallback
          uncachedTexts.forEach(t => cache.set(t, t));
        }
      }

      return applyTranslations(checklistName, rooms, cache);
    } catch (error) {
      console.error('Checklist translation error:', error);
      // Return original on error
      return { name: checklistName, rooms };
    } finally {
      if (mountedRef.current) {
        setIsTranslating(false);
      }
    }
  }, [getLanguageCache]);

  // Apply cached translations to checklist structure
  const applyTranslations = (
    checklistName: string,
    rooms: Room[],
    cache: Map<string, string>
  ): TranslatedChecklist => {
    const translatedName = cache.get(checklistName) || checklistName;
    
    const translatedRooms = rooms.map(room => ({
      ...room,
      name: cache.get(room.name) || room.name,
      tasks: room.tasks.map(task => {
        const taskObj = normalizeTask(task);
        const translatedText = cache.get(taskObj.text) || taskObj.text;
        return {
          ...taskObj,
          text: translatedText,
        };
      }),
    }));

    return { name: translatedName, rooms: translatedRooms };
  };

  // Translate a single text (for inventory items, notes, etc.)
  const translateText = useCallback(async (
    text: string,
    targetLanguage?: string
  ): Promise<string> => {
    if (!text) return text;
    
    const lang = targetLanguage || getStoredLanguage();
    if (lang === 'en') return text;

    const cache = getLanguageCache(lang);
    
    // Return cached if available
    if (cache.has(text)) {
      return cache.get(text)!;
    }

    try {
      // Check Supabase cache first
      const { data: dbCached } = await supabase
        .from('translations_cache')
        .select('translated_text')
        .eq('language', lang)
        .eq('source_text', text)
        .single();

      if (dbCached) {
        cache.set(text, dbCached.translated_text);
        return dbCached.translated_text;
      }

      // Call translation API
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: { texts: [text], targetLanguage: lang }
      });

      if (!error && data?.translations?.[text]) {
        const translated = data.translations[text];
        cache.set(text, translated);
        return translated;
      }
    } catch (error) {
      console.error('Text translation error:', error);
    }

    return text;
  }, [getLanguageCache]);

  // Batch translate multiple texts (for inventory, notes, etc.)
  const translateTexts = useCallback(async (
    texts: string[],
    targetLanguage?: string
  ): Promise<Record<string, string>> => {
    const lang = targetLanguage || getStoredLanguage();
    const result: Record<string, string> = {};
    
    if (lang === 'en') {
      texts.forEach(t => result[t] = t);
      return result;
    }

    const cache = getLanguageCache(lang);
    const uncached: string[] = [];

    // Check session cache first
    texts.forEach(t => {
      if (cache.has(t)) {
        result[t] = cache.get(t)!;
      } else if (t) {
        uncached.push(t);
      }
    });

    if (uncached.length === 0) {
      return result;
    }

    try {
      // Check Supabase cache
      const { data: dbCached } = await supabase
        .from('translations_cache')
        .select('source_text, translated_text')
        .eq('language', lang)
        .in('source_text', uncached);

      const stillUncached: string[] = [];
      
      if (dbCached) {
        dbCached.forEach(c => {
          cache.set(c.source_text, c.translated_text);
          result[c.source_text] = c.translated_text;
        });
        const cachedSources = new Set(dbCached.map(c => c.source_text));
        uncached.forEach(t => {
          if (!cachedSources.has(t)) {
            stillUncached.push(t);
          }
        });
      } else {
        stillUncached.push(...uncached);
      }

      if (stillUncached.length > 0) {
        const { data, error } = await supabase.functions.invoke('translate-text', {
          body: { texts: stillUncached, targetLanguage: lang }
        });

        if (!error && data?.translations) {
          Object.entries(data.translations).forEach(([source, translated]) => {
            cache.set(source, translated as string);
            result[source] = translated as string;
          });
        } else {
          stillUncached.forEach(t => result[t] = t);
        }
      }
    } catch (error) {
      console.error('Batch translation error:', error);
      uncached.forEach(t => {
        if (!result[t]) result[t] = t;
      });
    }

    return result;
  }, [getLanguageCache]);

  // Clear cache for a specific language (used when switching languages)
  const clearCache = useCallback((lang?: string) => {
    if (lang) {
      checklistTranslationCache.delete(lang);
    } else {
      checklistTranslationCache.clear();
    }
  }, []);

  return {
    translateChecklist,
    translateText,
    translateTexts,
    clearCache,
    isTranslating,
  };
};

// Export cache clear function for use in language switching
export const clearTranslationCache = () => {
  checklistTranslationCache.clear();
};
